import axios from 'axios'
import { useRouter } from 'next/router'
import type { ReactElement } from 'react'
import { createContext, useContext, useEffect, useState } from 'react'

import { useBrand } from './useBrand'
import { usePostMessage } from './usePostMessage'

type Props = {
  children: ReactElement
}

type DealerContextType = {
  dealer: any
  preferredDealerName: string | null
  retailerType: string | null
}

const DealerContext = createContext<DealerContextType | undefined>(undefined)

const DealerProvider = ({ children }: Props) => {
  const router = useRouter()
  const brand = useBrand()
  const [dealerObj, setdealerObj] = useState(false)
  const [preferredDealer, setPreferredDealer] = useState('')
  const [preferredDealerName, setPreferredDealerName] = useState<string | null>(
    null,
  )
  const [retailerType, setRetailerType] = useState<string | null>(null)
  const { dealer, centre, centreId, dealerID, dealerId, otso, olso } =
    router.query
  const query = dealer || centre || centreId || dealerID || dealerId || ''

  const { data: messageData, received } = usePostMessage(
    'gb_form_getPreferredDealer',
    'gb_aem_getPreferredDealer',
  )

  useEffect(() => {
    if (received && messageData?.dealer) {
      setPreferredDealer(messageData.dealer)
      if (messageData.dealerName) {
        setPreferredDealerName(messageData.dealerName)
      }
      if (messageData.retailerType) {
        setRetailerType(messageData.retailerType)
      }
    }
  }, [received, messageData])

  useEffect(() => {
    async function getDealer(queryDealer) {
      try {
        const response = await axios.post(
          `${process.env.NEXT_PUBLIC_API}/dealers`,
          {
            dealer: queryDealer,
            servicing: !!otso || !!olso,
          },
        )

        if (!response?.data?.[0]?._source) {
          // eslint-disable-next-line no-console
          console.log(`[GB] No dealer found in API response`)
          return false
        }

        if (
          response?.data[0]?._source?.uuid ===
          (brand === 'lexus'
            ? process.env.NEXT_PUBLIC_NATIONAL_DEALER_ID_LEXUS
            : process.env.NEXT_PUBLIC_NATIONAL_DEALER_ID_TOYOTA)
        ) {
          // eslint-disable-next-line no-console
          console.log(
            `[GB] National dealer identified by UUID: ${response.data[0]._source.uuid}`,
          )
          return false
        }

        const dealerBrand = response.data[0]._source.brands[0].toLowerCase()
        if (dealerBrand !== brand) {
          // eslint-disable-next-line no-console
          console.log(
            `[GB] Brand mismatch: dealer is ${dealerBrand}, form is ${brand}`,
          )
          return false
        }

        return response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(`[GB] API error:`, error)
        return false
      }
    }

    async function checkDealer() {
      let dealerQuery = query

      if (!dealerQuery && preferredDealer && retailerType === 'outlet') {
        // eslint-disable-next-line no-console
        console.log(
          `[GB] Using preferred dealer from postMessage (outlet): ${preferredDealer}`,
        )
        dealerQuery = preferredDealer
      }

      if (dealerQuery) {
        const res = await getDealer(dealerQuery)
        if (res) {
          setdealerObj(res[0]._source)
          // eslint-disable-next-line no-console
          console.log(
            `[GB] Dealer/Center found: ${res[0]._source.brands[0]} - ${res[0]._source.name} - ${res[0]._source.applications.NMSC.externalID} - ${res[0]._source.uuid}`,
          )

          window.tngaDataLayer.dealer = {
            name: res[0]._source.name,
            dealerId: res[0]._source.uuid,
            city: res[0]._source.addresses[0].city,
            zipcode: res[0]._source.addresses[0].zip,
          }
        }
      }
    }

    checkDealer()
  }, [query, brand, preferredDealer, retailerType])

  return (
    <DealerContext.Provider
      value={{ dealer: dealerObj, preferredDealerName, retailerType }}
    >
      {children}
    </DealerContext.Provider>
  )
}

const useDealer = () => {
  const context = useContext(DealerContext)
  // For backwards compatibility, return dealer directly if context exists
  return context?.dealer
}

const useDealerContext = () => {
  return useContext(DealerContext)
}

export { DealerProvider, useDealer, useDealerContext }
